import React, { useState } from "react";
import { Button, Input, Modal, notification } from "antd";
import Item from "antd/es/list/Item";

function ZipCodeInput({ zone, zipCodesList, setZipCodesList, outsideZone, setOutsideZone }) {
  const [currentZip, setCurrentZip] = useState("");

  const getUniqueZips = (...arrays) => Array.from(new Set(arrays.flat()));

  const handleAddZipCodes = () => {
    if (!currentZip.trim()) return;
    
    const zipCodesArray = currentZip
      .split(",")
      .map((item) => item.trim())
      .filter(item => item !== "");

    const isPrimary = zone === 'primary';
    const currentSet = new Set(isPrimary ? zipCodesList : outsideZone.zipCodes);
    const oppositeSet = new Set(isPrimary ? outsideZone.zipCodes : zipCodesList);

    const duplicatesInCurrent = zipCodesArray.filter(zip => currentSet.has(zip));
    const duplicatesInOpposite = zipCodesArray.filter(zip => oppositeSet.has(zip));
    const newZips = zipCodesArray.filter(zip => !currentSet.has(zip) && !oppositeSet.has(zip));

    if (duplicatesInOpposite.length > 0) {
      const duplicatesList = duplicatesInOpposite.join(", ");
      const count = duplicatesInOpposite.length;
      const oppositeZoneName = isPrimary ? "Outside Zone" : "Primary Service Area";
      const currentZoneName = isPrimary ? "Primary Service Area" : "Outside Zone";
      
      Modal.confirm({
        title: `Move Zip Codes from ${oppositeZoneName}?`,
        content: (
          <div>
            <p style={{ marginBottom: "12px" }}>
              <strong>{duplicatesList}</strong> {count === 1 ? 'is' : 'are'} currently in the{" "}
              <span style={{ color: isPrimary ? "#faad14" : "#52c41a", fontWeight: "bold" }}>
                {oppositeZoneName}
              </span>.
            </p>
            <p>
              Would you like to move {count > 1 ? 'them' : 'it'} to the{" "}
              <span style={{ color: isPrimary ? "#52c41a" : "#faad14", fontWeight: "bold" }}>
                {currentZoneName}
              </span>?
            </p>
          </div>
        ),
        okText: `Yes, Move to ${currentZoneName}`,
        cancelText: `No, Keep in ${oppositeZoneName}`,
        cancelButtonProps: { style: { marginBottom: "4px" } },
        onOk: () => {
          if (isPrimary) {
            const updatedOutsideZone = outsideZone.zipCodes.filter(zip => !duplicatesInOpposite.includes(zip));
            const allNewZips = [...newZips, ...duplicatesInOpposite];
            setZipCodesList(getUniqueZips(zipCodesList, allNewZips));
            setOutsideZone({ ...outsideZone, zipCodes: updatedOutsideZone });
          } else {
            const updatedPrimaryZone = zipCodesList.filter(zip => !duplicatesInOpposite.includes(zip));
            const allNewZips = [...newZips, ...duplicatesInOpposite];
            setZipCodesList(updatedPrimaryZone);
            setOutsideZone({ ...outsideZone, zipCodes: getUniqueZips(outsideZone.zipCodes, allNewZips) });
          }
          setCurrentZip("");
          notification.success({
            message: 'Success',
            description: `Moved ${count} zip code${count > 1 ? 's' : ''} to ${currentZoneName}`,
            duration: 3,
            placement: 'topRight'
          });
        },
        onCancel: () => {
          if (newZips.length > 0) {
            if (isPrimary) {
              setZipCodesList(getUniqueZips(zipCodesList, newZips));
            } else {
              setOutsideZone({ ...outsideZone, zipCodes: getUniqueZips(outsideZone.zipCodes, newZips) });
            }
            notification.success({
              message: 'Success',
              description: `Added ${newZips.length} new zip code${newZips.length > 1 ? 's' : ''}`,
              duration: 3,
              placement: 'topRight'
            });
          }
          setCurrentZip("");
        }
      });
      return;
    }

    if (duplicatesInCurrent.length > 0) {
      const duplicatesList = duplicatesInCurrent.join(", ");
      const count = duplicatesInCurrent.length;
      
      Modal.confirm({
        title: "Duplicate Zip Codes Detected",
        content: (
          <div>
            <p style={{ marginBottom: "12px" }}>
              {count} zip code{count > 1 ? 's are' : ' is'} already in the list: {duplicatesList}
            </p>
            {newZips.length > 0 ? (
              <p>Would you like to add the <strong>{newZips.length} new zip code{newZips.length > 1 ? 's' : ''}</strong>?</p>
            ) : (
              <p style={{ color: "#8c8c8c" }}>All zip codes are already in the list.</p>
            )}
          </div>
        ),
        okText: newZips.length > 0 ? "Yes, Add New Ones" : "OK",
        cancelText: "Cancel",
        okButtonProps: { type: newZips.length > 0 ? "primary" : "default" },
        onOk: () => {
          if (newZips.length > 0) {
            if (isPrimary) {
              setZipCodesList(getUniqueZips(zipCodesList, newZips));
            } else {
              setOutsideZone({ ...outsideZone, zipCodes: getUniqueZips(outsideZone.zipCodes, newZips) });
            }
            notification.success({
              message: 'Success',
              description: `Added ${newZips.length} new zip code${newZips.length > 1 ? 's' : ''}`,
              duration: 3,
              placement: 'topRight'
            });
          }
          setCurrentZip("");
        },
        onCancel: () => setCurrentZip("")
      });
      return;
    }

    if (isPrimary) {
      setZipCodesList(getUniqueZips(zipCodesList, zipCodesArray));
    } else {
      setOutsideZone({ ...outsideZone, zipCodes: getUniqueZips(outsideZone.zipCodes, zipCodesArray) });
    }
    setCurrentZip("");
    notification.success({
      message: 'Success',
      description: `Added ${zipCodesArray.length} zip code${zipCodesArray.length > 1 ? 's' : ''}`,
      duration: 3,
      placement: 'topRight'
    });
  };

  return (
    <div className="flex flex-col gap-2 w-full items-start">
      <label style={{ fontWeight: "bold", fontSize: "14px" }}>
        Enter Zip Code{zone === 'outside' && 's'}
      </label>
      <Item
        name={`${zone}ZipCodes`}
        className="profile_input_wrapper"
        style={{ marginBottom: 0, width: "100%" }}
      >
        <Input
          value={currentZip}
          onChange={(e) => setCurrentZip(e.target.value)}
          placeholder={zone === 'primary' ? "e.g., 10001, 10002, 10003" : "e.g., 10004, 10005, 10006"}
          size="large"
        />
        <Button 
          type="primary"
          style={{ marginTop: 10 }} 
          onClick={handleAddZipCodes}
          disabled={!currentZip.trim()}
        >
          Add {zone === 'outside' && 'Outside Zone '}Zip Codes
        </Button>
      </Item>
    </div>
  );
}

export default ZipCodeInput;